import type { Extensions } from "@tiptap/core";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import { LinkBackspaceDelete } from "./keymaps/LinkBackspaceDelete";
import { MediaEmbedNode } from "./nodes/MediaEmbed";

export type CompassEditorFeatures = {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strike?: boolean;
    heading?: boolean | { levels?: (1 | 2 | 3 | 4 | 5 | 6)[] };
    blockquote?: boolean;
    bulletList?: boolean;
    orderedList?: boolean;
    code?: boolean;
    link?: boolean;
    table?: boolean;
    image?: boolean;
    mediaEmbed?: boolean;
    placeholder?: string | false;
};

const ALL_FEATURES_ON: Required<Omit<CompassEditorFeatures, "placeholder">> & {
    placeholder?: string | false;
} = {
    bold: true,
    italic: true,
    underline: true,
    strike: true,
    heading: true,
    blockquote: true,
    bulletList: true,
    orderedList: true,
    code: true,
    link: true,
    table: true,
    image: false,
    mediaEmbed: false,
    placeholder: undefined,
};

// Builds the canonical Compass TipTap extension list. Use the same call from:
//   - web PostEditor
//   - navigator PostEditor
//   - admin BlogContentEditor
//   - server-side @tiptap/html generateJSON (markdownToTipTapDoc backfill)
// Read-side rendering uses a JSON walker (TipTapRenderer) instead of TipTap
// extensions — no need to load @tiptap/pm at view time. Indentation /
// CustomParagraph / CustomHeading are intentionally absent — they were a
// misfeature in the markdown era. Native list nesting via StarterKit covers
// ul/ol/ul→ol/ol→ul without any indent attribute.
export function getCompassTipTapExtensions(opts: CompassEditorFeatures = {}): Extensions {
    const features = { ...ALL_FEATURES_ON, ...opts };

    const headingConfig =
        typeof features.heading === "object"
            ? { levels: features.heading.levels ?? [1, 2, 3, 4, 5, 6] }
            : { levels: [1, 2, 3, 4, 5, 6] as (1 | 2 | 3 | 4 | 5 | 6)[] };

    const ext: Extensions = [
        StarterKit.configure({
            bold: features.bold ? {} : false,
            italic: features.italic ? {} : false,
            strike: features.strike ? {} : false,
            heading: features.heading ? headingConfig : false,
            blockquote: features.blockquote ? {} : false,
            bulletList: features.bulletList ? {} : false,
            orderedList: features.orderedList ? {} : false,
            listItem: features.bulletList || features.orderedList ? {} : false,
            code: features.code ? {} : false,
            codeBlock: features.code ? {} : false,
        }),
    ];

    if (features.underline) ext.push(Underline);
    if (features.link) {
        ext.push(
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
            }),
        );
        ext.push(LinkBackspaceDelete);
    }
    if (features.table) {
        ext.push(Table.configure({ resizable: false }));
        ext.push(TableRow);
        ext.push(TableHeader);
        ext.push(TableCell);
    }
    if (features.image) {
        ext.push(Image.configure({ inline: false, allowBase64: false }));
    }
    if (features.mediaEmbed) {
        ext.push(MediaEmbedNode);
    }
    if (features.placeholder !== false) {
        ext.push(
            Placeholder.configure({
                placeholder: ({ editor }) => {
                    const { doc } = editor.state;
                    const isDocEmpty =
                        doc.childCount === 1 &&
                        doc.firstChild?.type.name === "paragraph" &&
                        doc.firstChild?.childCount === 0;
                    return isDocEmpty ? features.placeholder || "Type here..." : "";
                },
                showOnlyWhenEditable: true,
                showOnlyCurrent: false,
            }),
        );
    }

    return ext;
}

export { MediaEmbedNode } from "./nodes/MediaEmbed";
export type { MediaEmbedKind, MediaEmbedVariant } from "./nodes/MediaEmbed";
